import * as Sentry from '@sentry/react';
import { getActiveEnvironment, isProductionEnvironment } from './environment';
import { logger } from './logger';

export const initSentry = () => {
  if (process.env.DISABLE_SENTRY) {
    return;
  }

  Sentry.init({
    dsn: process.env.SENTRY_DSN_URL,
    environment: getActiveEnvironment(),
    tracesSampleRate: isProductionEnvironment() ? 0.1 : 1.0,
    integrations: [Sentry.browserTracingIntegration()],
    ignoreErrors: [
      // PayPal/Zoid third-party lifecycle events
      /zoid destroyed all components/,
      // Network errors (do NOT include browser fetch-failure variants — may mask backend outages)
      'Network Error',
      /Load failed/,
      // IndexedDB browser storage errors (transient, non-actionable)
      /Connection to Indexed Database server lost/,
      /Database deleted by request/,
      /Object store cannot be found/,
      // Cross-origin security errors from third-party iframes
      /Blocked a frame with origin/,
      // Timeout promise rejections from third-party SDKs
      /Non-Error promise rejection captured with value: Timeout/,
      // detectIncognito browser detection library
      /detectIncognito cannot determine/,
      // Android WebView native errors
      'Java object is gone',
      // PayPal iframe postMessage errors
      'No ack for postMessage',
      // reCAPTCHA configuration errors
      'Missing required parameters: sitekey',
      // User-initiated navigation aborts
      'AbortError'
    ],
    denyUrls: [
      // PayPal SDK
      /sdk\/js/,
      // Recurly SDK
      /v4\/recurly\.js/
    ],
    beforeSend(event) {
      const errorValue = event.exception?.values?.[0]?.value;

      if (errorValue) {
        // Filter expected Firebase Auth user-flow errors (not actionable)
        // Do NOT filter auth/wrong-password (needed for brute-force detection)
        // Do NOT filter auth/network-request-failed (could indicate real infra issues)
        if (
          errorValue.includes('auth/popup-blocked') ||
          errorValue.includes('auth/popup-closed-by-user') ||
          errorValue.includes('auth/cancelled-popup-request')
        ) {
          return null;
        }

        // Filter DOM nodeType errors only when all stack frames are third-party
        // (no /src/ frames means the error originated entirely in a third-party SDK)
        if (errorValue.includes('nodeType')) {
          const frames = event.exception?.values?.[0]?.stacktrace?.frames;
          if (frames && frames.length > 0) {
            const allThirdParty = frames.every((frame) => !frame.filename?.match(/^(\.\/|webpack:\/\/\/?)src\//));
            if (allThirdParty) {
              return null;
            }
          }
        }
      }

      return event;
    }
  });
};

export const sentryCaptureException = (message: string, error?: any, context?: Parameters<typeof Sentry.captureException>[1]) => {
  logger.error(message, error, context);

  // Normalize non-Error objects so Sentry groups by the descriptive message
  // rather than the generic "Object captured as exception with keys: body, status"
  let normalizedError: Error;
  let normalizedContext = context;
  if (error instanceof Error) {
    normalizedError = error;
  } else {
    normalizedError = new Error(message);
    // Preserve original non-Error value as extra context for debugging
    if (error !== undefined && error !== null) {
      normalizedContext = {
        ...context,
        extra: {
          ...(context as any)?.extra,
          originalError: error
        }
      } as Parameters<typeof Sentry.captureException>[1];
    }
  }

  const enhancedContext = {
    ...normalizedContext,
    extra: {
      ...(normalizedContext as any)?.extra,
      customErrorMessage: message
    }
  };
  Sentry.captureException(normalizedError, enhancedContext);
};

export const sentryCaptureMessage = (message: string, context?: Parameters<typeof Sentry.captureMessage>[1]) => {
  logger.debug('📝 Sentry capture message', message, context);
  Sentry.captureMessage(message, context);
};

export const setSentryUser = (userOrUserId: string | Parameters<typeof Sentry.setUser>[0]) => {
  const user = typeof userOrUserId === 'string' ? { id: userOrUserId } : userOrUserId;
  Sentry.setUser(user);
};

export const setSentryTag = (key: string, value: Parameters<typeof Sentry.setTag>[1]) => {
  logger.debug('🏷️ Setting Sentry tag', key, value);
  Sentry.setTag(key, value);
};

export const addSentryBreadcrumb = (breadcrumb: Parameters<typeof Sentry.addBreadcrumb>[0]) => {
  logger.debug('🍞 Adding Sentry breadcrumb', breadcrumb);
  Sentry.addBreadcrumb(breadcrumb);
};

export const withSentryScope = (callback: (scope: Sentry.Scope) => void) => {
  logger.debug('🔍 Executing with Sentry scope');
  Sentry.withScope(callback);
};
